import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout.jsx';
import ApiClient from '../../utils/ApiClient.js';
import { firstResolvedDisplayName } from '../../utils/DisplayValueUtils.js';
import { getFirstAttributeValue } from '../../utils/KeycloakAttributeUtils.js';
import { getUserTypeDisplayLabel, normalizeUserTypeCode } from '../../utils/UserTypeUtils.js';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import ProfileCompletionRadial from '../../components/ProfileCompletionRadial/ProfileCompletionRadial.jsx';

const formatLastUpdatedForLocalDisplay = (value) => {
  if (!value) {
    return 'Never';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  const resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  const formatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  const formattedValue = formatter.format(parsedDate);

  return resolvedTimeZone ? `${formattedValue} (${resolvedTimeZone})` : formattedValue;
};

const parseBooleanPreference = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalizedValue = String(value ?? '').trim().toLowerCase();
  if (normalizedValue === 'true') {
    return true;
  }
  if (normalizedValue === 'false') {
    return false;
  }

  return fallback;
};

const hasBooleanPreferenceValue = (value) => {
  const normalizedValue = String(value ?? '').trim().toLowerCase();
  return normalizedValue === 'true' || normalizedValue === 'false';
};

const getTokenAttributeValue = (tokenDetails, keys, fallback = '') => {
  const directClaimValue = getFirstAttributeValue(tokenDetails, keys, '');
  if (directClaimValue !== undefined && directClaimValue !== null && directClaimValue !== '') {
    return directClaimValue;
  }
  return getFirstAttributeValue(tokenDetails?.attributes, keys, fallback);
};

const formatDateOnlyForDisplay = (value, fallback = 'Not set') => {
  const rawValue = String(value ?? '').trim();
  if (!rawValue) {
    return fallback;
  }

  const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  const dateOnlyMatch = rawValue.match(dateOnlyPattern);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const monthIndex = Number(dateOnlyMatch[2]) - 1;
    const day = Number(dateOnlyMatch[3]);
    const utcDate = new Date(Date.UTC(year, monthIndex, day));
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(utcDate);
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsedDate);
};

const ProfilePage = ({ authDetails, onSignOut }) => {
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [localPhotoUrl, setLocalPhotoUrl] = useState(null);
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(true);
  const [profileDetails, setProfileDetails] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [supervisorDetails, setSupervisorDetails] = useState(null);
  const [isLoadingSupervisor, setIsLoadingSupervisor] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const tokenDetails = authDetails?.identity?.tokenParsed ?? {};
  const subject = tokenDetails.sub ?? authDetails?.identity?.subject ?? null;
  const tokenSlackNotifications = getTokenAttributeValue(tokenDetails, ['slackNotifications'], '');
  const tokenEmailNotifications = getTokenAttributeValue(tokenDetails, ['emailNotifications'], '');
  const tokenJobTitle = getTokenAttributeValue(tokenDetails, ['jobTitle', 'JobTitle'], '');
  const tokenUserType = normalizeUserTypeCode(
    getTokenAttributeValue(tokenDetails, ['UserType', 'userType'], '')
  );
  const tokenContractNumber = getTokenAttributeValue(
    tokenDetails,
    ['contractNumber', 'ContractNumber'],
    ''
  );
  const tokenContractExpirationDate = getTokenAttributeValue(
    tokenDetails,
    ['contractExpirationDate', 'ContractExpirationDate'],
    ''
  );
  const profileAttributes =
    profileDetails?.attributes && typeof profileDetails.attributes === 'object'
      ? profileDetails.attributes
      : {};
  const supervisorId =
    getFirstAttributeValue(profileAttributes, ['supervisor', 'Supervisor'], tokenDetails.supervisor ?? '');
  const supervisorDisplayName =
    firstResolvedDisplayName([
      supervisorDetails?.name,
      `${supervisorDetails?.firstName || ''} ${supervisorDetails?.lastName || ''}`.trim(),
      supervisorDetails?.preferred_username,
      supervisorDetails?.username,
    ], 'Unassigned');
  const slackNotificationsValue = getFirstAttributeValue(
    profileAttributes,
    ['slackNotifications'],
    tokenSlackNotifications
  );
  const slackNotificationsEnabled = parseBooleanPreference(
    slackNotificationsValue,
    false
  );
  const emailNotificationsValue = getFirstAttributeValue(
    profileAttributes,
    ['emailNotifications'],
    tokenEmailNotifications
  );
  const emailNotificationsEnabled = parseBooleanPreference(
    emailNotificationsValue,
    false
  );
  const jobTitleValue = getFirstAttributeValue(profileAttributes, ['jobTitle', 'JobTitle'], tokenJobTitle);
  const userTypeCode = normalizeUserTypeCode(
    getFirstAttributeValue(
      profileAttributes,
      ['UserType', 'userType'],
      profileDetails?.UserType ?? profileDetails?.userType ?? profileDetails?.user_type ?? tokenUserType
    )
  );
  const contractNumberValue = getFirstAttributeValue(
    profileAttributes,
    ['contractNumber', 'ContractNumber'],
    tokenContractNumber
  );
  const contractExpirationDateValue = getFirstAttributeValue(
    profileAttributes,
    ['contractExpirationDate', 'ContractExpirationDate'],
    tokenContractExpirationDate
  );
  const isContractor = userTypeCode === 'CTR';
  const lastUpdatedValue =
    getFirstAttributeValue(profileAttributes, ['lastUpdated', 'LastUpdated']) ||
    tokenDetails.lastUpdated ||
    tokenDetails.lastUpdate ||
    '';
  const profile = {
    firstName: profileDetails?.firstName || tokenDetails.given_name || 'Jordan',
    lastName: profileDetails?.lastName || tokenDetails.family_name || 'Matthews',
    username:
      profileDetails?.username ||
      profileDetails?.preferred_username ||
      tokenDetails.preferred_username ||
      'jmatthews',
    email: profileDetails?.email || tokenDetails.email || 'jordan.matthews@jcwa.dhs.gov',
    jobTitle: jobTitleValue || 'Not set',
    userType: getUserTypeDisplayLabel(userTypeCode, 'Not set'),
    contractNumber: contractNumberValue || 'Not set',
    contractExpirationDate: formatDateOnlyForDisplay(contractExpirationDateValue),
    slackNotifications: slackNotificationsEnabled ? 'On' : 'Off',
    emailNotifications: emailNotificationsEnabled ? 'On' : 'Off',
    supervisor: supervisorDisplayName,
    lastUpdated: lastUpdatedValue || 'Never',
  };
  const formattedLastUpdated = formatLastUpdatedForLocalDisplay(profile.lastUpdated);
  const slackNotificationsToggleId = 'profile-slack-notifications-readonly';
  const emailNotificationsToggleId = 'profile-email-notifications-readonly';
  const completionChecks = [
    Boolean(profileDetails?.firstName || tokenDetails.given_name),
    Boolean(profileDetails?.lastName || tokenDetails.family_name),
    Boolean(profileDetails?.username || profileDetails?.preferred_username || tokenDetails.preferred_username),
    Boolean(profileDetails?.email || tokenDetails.email),
    Boolean(String(supervisorId || '').trim()),
    hasBooleanPreferenceValue(slackNotificationsValue),
    hasBooleanPreferenceValue(emailNotificationsValue),
    Boolean(localPhotoUrl || profilePhotoUrl),
    Boolean(String(lastUpdatedValue || '').trim()),
    Boolean(String(jobTitleValue || '').trim()),
    Boolean(userTypeCode),
  ];
  if (isContractor) {
    completionChecks.push(Boolean(String(contractNumberValue || '').trim()));
    completionChecks.push(Boolean(String(contractExpirationDateValue || '').trim()));
  }
  const completedFieldsCount = completionChecks.filter(Boolean).length;
  const profileCompletionPercent = Math.round((completedFieldsCount / completionChecks.length) * 100);

  useEffect(() => {
    let isMounted = true;

    if (!subject) {
      setProfileDetails(null);
      setIsLoadingProfile(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchProfileDetails = async () => {
      setIsLoadingProfile(true);
      try {
        const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}`);

        if (response.status !== 200) {
          throw new Error(`API responded with ${response.status}`);
        }

        if (isMounted) {
          setProfileDetails(response.data ?? null);
        }
      } catch (error) {
        if (isMounted) {
          setProfileDetails(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false);
        }
      }
    };

    fetchProfileDetails();

    return () => {
      isMounted = false;
    };
  }, [subject]);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    if (!subject) {
      setIsLoadingPhoto(false);
      setProfilePhotoUrl(null);
      return () => {
        isMounted = false;
      };
    }

    const fetchProfilePhoto = async () => {
      try {
        const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}/picture`, {
          responseType: 'blob',
        });

        if (response.status !== 200) {
          throw new Error(`API responded with ${response.status}`);
        }

        const contentType = response.headers?.['content-type'] ?? '';
        if (contentType && !contentType.startsWith('image/')) {
          throw new Error(`Unexpected content type: ${contentType}`);
        }

        objectUrl = URL.createObjectURL(response.data);

        if (isMounted) {
          setProfilePhotoUrl(objectUrl);
        }
      } catch (error) {
        if (isMounted) {
          setProfilePhotoUrl(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingPhoto(false);
        }
      }
    };

    fetchProfilePhoto();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [subject]);

  useEffect(() => {
    return () => {
      if (localPhotoUrl) {
        URL.revokeObjectURL(localPhotoUrl);
      }
    };
  }, [localPhotoUrl]);

  useEffect(() => {
    let isMounted = true;
    const normalizedSupervisorId = String(supervisorId || '').trim();

    if (!normalizedSupervisorId) {
      setSupervisorDetails(null);
      setIsLoadingSupervisor(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchSupervisorDetails = async () => {
      setIsLoadingSupervisor(true);
      try {
        const response = await ApiClient.get(`/users/${encodeURIComponent(normalizedSupervisorId)}`);

        if (response.status !== 200) {
          throw new Error(`API responded with ${response.status}`);
        }

        if (isMounted) {
          setSupervisorDetails(response.data ?? null);
        }
      } catch (error) {
        if (isMounted) {
          setSupervisorDetails(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingSupervisor(false);
        }
      }
    };

    fetchSupervisorDetails();

    return () => {
      isMounted = false;
    };
  }, [supervisorId]);

  const handleEditProfile = () => {
    navigate('/editProfile');
  };

  const handlePhotoUploadClick = () => {
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    setPhotoUploadError('');

    if (localPhotoUrl) {
      URL.revokeObjectURL(localPhotoUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setLocalPhotoUrl(previewUrl);

    if (!subject) {
      setPhotoUploadError('Your profile identifier is missing. Please refresh and try again.');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    try {
      setIsUploadingPhoto(true);
      await ApiClient.post(`/users/${encodeURIComponent(subject)}/picture`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      setPhotoUploadError('We could not upload your profile photo. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const activePhotoUrl = localPhotoUrl ?? profilePhotoUrl;

  return (
    <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
      <section className="usa-section">
        <div className="grid-container">
          <div className="grid-row grid-gap">
            <div className="tablet:grid-col-8">
              <div className="usa-card">
                <div className="usa-card__container">
                  <div className="usa-card__header profile-card__header">
                    <h2 className="usa-card__heading">Profile</h2>
                    <button
                      type="button"
                      className="usa-button usa-button--outline profile-card__edit"
                      onClick={handleEditProfile}
                    >
                      <img
                        src="/icons/edit.png"
                        alt=""
                        aria-hidden="true"
                        className="profile-card__edit-icon"
                      />
                      Edit
                    </button>
                  </div>
                  <div className="usa-card__body">
                    {photoUploadError ? (
                      <div className="usa-alert usa-alert--error" role="alert">
                        <div className="usa-alert__body">
                          <p className="usa-alert__text">{photoUploadError}</p>
                        </div>
                      </div>
                    ) : null}
                    <div className="approval-requester profile-requester">
                      <div
                        className={
                          activePhotoUrl
                            ? 'approval-requester__avatar approval-requester__avatar--photo'
                            : 'approval-requester__avatar'
                        }
                        aria-hidden={!activePhotoUrl}
                      >
                        {activePhotoUrl && !isLoadingPhoto ? (
                          <img
                            className="approval-requester__avatar-image"
                            src={activePhotoUrl}
                            alt={`${profile.firstName} ${profile.lastName}`}
                          />
                        ) : null}
                      </div>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        aria-label="Upload profile photo"
                        className="profile-card__photo-input"
                        onChange={handlePhotoChange}
                      />
                      <button
                        type="button"
                        className="profile-card__photo-link"
                        onClick={handlePhotoUploadClick}
                        disabled={isUploadingPhoto}
                      >
                        {isUploadingPhoto ? 'Uploading...' : 'Update photo'}
                      </button>
                      <div className="approval-requester__info">
                        <p>
                          <span className="approval-key">First name</span>
                          <ValueWithSkeleton
                            value={profile.firstName}
                            loading={isLoadingProfile}
                            fallback="Not set"
                            width="12ch"
                          />
                        </p>
                        <p>
                          <span className="approval-key">Last name</span>
                          <ValueWithSkeleton
                            value={profile.lastName}
                            loading={isLoadingProfile}
                            fallback="Not set"
                            width="12ch"
                          />
                        </p>
                        <p>
                          <span className="approval-key">Username</span>
                          <ValueWithSkeleton
                            value={profile.username}
                            loading={isLoadingProfile}
                            fallback="Not set"
                            width="14ch"
                          />
                        </p>
                        <p>
                          <span className="approval-key">Email</span>
                          <ValueWithSkeleton
                            value={profile.email}
                            loading={isLoadingProfile}
                            fallback="Not set"
                            width="16ch"
                          />
                        </p>
                        <p>
                          <span className="approval-key">Job title</span>
                          <ValueWithSkeleton
                            value={profile.jobTitle}
                            loading={isLoadingProfile}
                            fallback="Not set"
                            width="14ch"
                          />
                        </p>
                        <p>
                          <span className="approval-key">User type</span>
                          <ValueWithSkeleton
                            value={profile.userType}
                            loading={isLoadingProfile}
                            fallback="Not set"
                            width="10ch"
                          />
                        </p>
                        {isContractor ? (
                          <>
                            <p>
                              <span className="approval-key">Contract number</span>
                              <ValueWithSkeleton
                                value={profile.contractNumber}
                                loading={isLoadingProfile}
                                fallback="Not set"
                                width="12ch"
                              />
                            </p>
                            <p>
                              <span className="approval-key">Contract expiration date</span>
                              <ValueWithSkeleton
                                value={profile.contractExpirationDate}
                                loading={isLoadingProfile}
                                fallback="Not set"
                                width="12ch"
                              />
                            </p>
                          </>
                        ) : null}
                        <p className="profile-card__slack-row">
                          <span className="approval-key">Slack notifications</span>
                          {isLoadingProfile ? (
                            <ValueWithSkeleton loading type="sentence" width="6ch" />
                          ) : (
                            <span className="usa-toggle profile-card__toggle">
                              <input
                                id={slackNotificationsToggleId}
                                className="usa-toggle__input"
                                type="checkbox"
                                checked={slackNotificationsEnabled}
                                aria-label={`Slack notifications: ${profile.slackNotifications}`}
                                readOnly
                                disabled
                              />
                              <label className="usa-toggle__label" htmlFor={slackNotificationsToggleId}>
                                <span className="profile-card__toggle-state">{profile.slackNotifications}</span>
                              </label>
                            </span>
                          )}
                        </p>
                        <p className="profile-card__slack-row">
                          <span className="approval-key">Email notifications</span>
                          {isLoadingProfile ? (
                            <ValueWithSkeleton loading type="sentence" width="6ch" />
                          ) : (
                            <span className="usa-toggle profile-card__toggle">
                              <input
                                id={emailNotificationsToggleId}
                                className="usa-toggle__input"
                                type="checkbox"
                                checked={emailNotificationsEnabled}
                                aria-label={`Email notifications: ${profile.emailNotifications}`}
                                readOnly
                                disabled
                              />
                              <label className="usa-toggle__label" htmlFor={emailNotificationsToggleId}>
                                <span className="profile-card__toggle-state">{profile.emailNotifications}</span>
                              </label>
                            </span>
                          )}
                        </p>
                        <p>
                          <span className="approval-key">Supervisor</span>
                          <ValueWithSkeleton
                            value={profile.supervisor}
                            loading={isLoadingProfile || isLoadingSupervisor}
                            fallback="Unassigned"
                            width="13ch"
                          />
                        </p>
                        <p className="profile-card__updated">
                          <span className="approval-key">Last updated</span>
                          <ValueWithSkeleton
                            value={formattedLastUpdated}
                            loading={isLoadingProfile}
                            fallback="Never"
                            width="18ch"
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="tablet:grid-col-4">
              <div className="usa-card profile-completion-card">
                <div className="usa-card__container">
                  <div className="usa-card__header">
                    <h2 className="usa-card__heading">Profile completion</h2>
                  </div>
                  <div className="usa-card__body profile-completion-card__body">
                    <ProfileCompletionRadial percent={profileCompletionPercent} />
                    <p className="profile-completion-card__summary">
                      {completedFieldsCount} of {completionChecks.length} profile fields complete
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </AppLayout>
  );
};

export default ProfilePage;
